<template>
  <section class="quote-features">
    <article v-if="isQA" class="fr-view">
      <h3>Product Inclusions</h3>
      <ul>
        <li v-for="(item, index) in descriptions" :key="`${index}_${item.name}`">{{ item }}</li>
      </ul>
    </article>
    <Accordion v-if="hospital" :accBtn="'Hospital Cover'">
      <h3>Hospital</h3>
      <p>
        {{ hospital.description }}
      </p>
      <div v-if="includedFeatures.length > 0">
        <h4>What's covered?</h4>
        <p>All clinical categories are included under your level of cover.</p>
        <div v-for="(item, index) in includedFeatures" :key="`${index}_${item.name}`">
          <h5>{{ item.name }}</h5>
          <p v-if="item.description">{{ item.description }}</p>
          <p v-else>No description.</p>
        </div>
      </div>
      <div v-if="restrictedFeatures.length > 0">
        <h4>What's restricted?</h4>
        <div v-if="!isGoldProduct(hospital.name)">
          <p>The restricted clinical categories excluded are:</p>
          <div v-for="(item, index) in restrictedFeatures" :key="`${index}_${item.name}`">
            <h5>{{ item.name }}</h5>
            <p v-if="item.description">{{ item.description }}</p>
            <p v-else>No description.</p>
          </div>
        </div>
        <div v-else><p>There are no restricted clinical categories.</p></div>
      </div>
      <div v-if="excludedFeatures.length > 0">
        <h4>What's excluded?</h4>
        <p>The clinical categories excluded are:</p>
        <div v-for="(item, index) in excludedFeatures" :key="`${index}_${item.name}`">
          <h5>{{ item.name }}</h5>
          <p v-if="item.description">{{ item.description }}</p>
          <p v-else>No description.</p>
        </div>
      </div>
    </Accordion>
    <Accordion v-if="extras" :accBtn="'Extras Cover'">
      <h3>Extras</h3>
      <p>
        {{ extras.description }}
      </p>
      <p>
        All of the goods and services claimable under extras cover have annual per person limits. Once the annual limit has been reached on a service, no further benefits are
        payable in that financial year. Most limits re-set on 1 July each year. Benefits, limits and payment conditions are assessed according to the date of service. Benefits and
        limits are subject to change.
      </p>
      <div v-for="(item, index) in includedExtrasModalities" :key="`${index}_${item.name}`">
        <template v-if="item.value.toLowerCase().includes('flexi-limit')">
          <h5>{{ item.name }}</h5>
          <ul>
            <li>
              <strong>Waiting Period:</strong>
              {{ item.waitingPeriod ? item.waitingPeriod : 'No waiting period' }}
            </li>
            <li>
              <strong>Annual Limit (included with Flexi-limit):</strong>
              {{ extras.flexiLimitValue }}
            </li>
          </ul>
          <p v-if="item.description">{{ item.description }}</p>
          <p v-else>No description.</p>
          <p>Combined annual limit for {{ flexiLimitFeatures.join(', ') }}</p>
        </template>
        <template v-else-if="item.value.toLowerCase().includes('sub limits apply')">
          <h5>{{ item.name }}</h5>
          <ul>
            <li>
              <strong>Waiting Period:</strong>
              {{ item.waitingPeriod ? item.waitingPeriod : 'No waiting period' }}
            </li>
            <li>
              <strong>Annual Limit:</strong>
              {{ item.value }}
            </li>
          </ul>
          <p v-if="item.description">{{ item.description }}</p>
          <p v-else>No description.</p>
          <p>{{ copy.FLEXILIMITS_SUBLIMITS_INFORMATION }}</p>
        </template>
        <template v-else-if="!item.flexiLimit">
          <h5>{{ item.name }}</h5>
          <ul>
            <li>
              <strong>Waiting Period:</strong>
              {{ item.waitingPeriod ? item.waitingPeriod : 'No waiting period' }}
            </li>
            <li>
              <strong>Annual Limit:</strong>
              {{ item.value }}
            </li>
          </ul>
          <p v-if="item.description">{{ item.description }}</p>
          <p v-else>No description.</p>
        </template>
      </div>
    </Accordion>
    <div id="info-text" v-html="informationText" v-if="showInformationText" />
    <p v-if="hospital && !isQA">
      <a :href="hospital.guideUrl" target="_blank" aria-label="Download product guide">Download {{ hospital.name }} product guide</a>
    </p>
    <p v-if="showExtras && !isQA">
      <a :href="extras.guideUrl" target="_blank" aria-label="Download product guide">Download {{ extras.name }} product guide</a>
    </p>
    <p v-if="combinedUrl && isQA">
      <a :href="combinedUrl" target="_blank" aria-label="Download product guide">Download {{ quote.name }} product guide</a>
    </p>
  </section>
</template>

<script>
import Accordion from '@/components/Accordion/Accordion.vue';
import copy from '@general/js/constants/Copy';
import { isGoldProduct } from '@general/js/utils/QuoteAcceptanceUtils';
import { ProductType } from '@general/js/enums/ProductTypeEnum';
import { ProductFeatureType } from '@general/js/enums/ProductFeatureTypeEnum';
import { getUniqueProductGuides } from '@general/js/utils';

export default {
  name: 'QuoteFeatures',

  components: { Accordion },

  data() {
    return {
      hospital: null,
      extras: null,
      package: null,
      hospitalFeatures: null,
      extraFeatures: null,
      flexiLimit: null,
      copy,
    };
  },

  props: {
    quote: {
      type: Object,
      required: true,
    },

    informationText: {
      type: String,
      default:
        '<p>The product guide and information below provide details of what is and is not covered, including any waiting periods, benefit limits and pre-existing conditions, exclusions, restrictions and excesses. Please read these carefully.</p>',
    },

    showInformationText: {
      type: Boolean,
      default: true,
      required: false,
    },

    isQA: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    includedFeatures() {
      return this.hospitalFeatures.filter((x) => {
        return x.value.toLowerCase() === ProductFeatureType.INCLUDED;
      });
    },
    restrictedFeatures() {
      return this.hospitalFeatures.filter((x) => {
        return x.value.toLowerCase() === ProductFeatureType.RESTRICTED;
      });
    },
    excludedFeatures() {
      return this.hospitalFeatures.filter((x) => {
        return x.value.toLowerCase() === ProductFeatureType.EXCLUDED;
      });
    },
    includedExtrasModalities() {
      return this.extraFeatures.filter((x) => {
        return x.value.toLowerCase() !== ProductFeatureType.EXCLUDED;
      });
    },
    flexiLimitFeatures() {
      return this.extraFeatures
        .filter((feature) => {
          return feature.value.toLowerCase().includes('flexi-limit');
        })
        .map((feature) => {
          return feature.name;
        });
    },
    showExtras() {
      return this.extras && !this.package;
    },
    combinedUrl() {
      const productUrl = getUniqueProductGuides(this.quote.products, this.quote.name);
      return productUrl ? productUrl[0].guideUrl : null;
    },

    descriptions() {
      if (!this.quote.products || this.quote.products.length === 0) return [];

      return this.quote.products
        .filter((product) => {
          return product.description;
        })
        .map((product) => {
          return product.description;
        });
    },
  },
  methods: {
    isGoldProduct,
  },
  mounted() {
    this.hospital = this.quote.products.filter((x) => {
      return x.type.toLowerCase() === ProductType.HOSPITAL_ONLY || x.type.toLowerCase() === ProductType.PACKAGE;
    })[0];
    this.hospitalFeatures = this.hospital?.features.filter((x) => {
      return x.featureType.toLowerCase() === ProductType.HOSPITAL_ONLY;
    });
    this.extras = this.quote.products.filter((x) => {
      return x.type.toLowerCase() === ProductType.EXTRAS_ONLY || x.type.toLowerCase() === ProductType.PACKAGE;
    })[0];
    // DE2E-14333: Find the corresponding flexi-limit waiting period for the product feature
    this.extraFeatures = this.extras?.features
      .filter((x) => {
        return x.featureType.toLowerCase() === ProductType.EXTRAS_ONLY;
      })
      .map((item) => {
        if (item.value.toLowerCase().includes('flexi-limit')) {
          const correspondingItem = this.extras?.features.find((i) => {
            return i.name === item.name && i.flexiLimit;
          });
          if (correspondingItem) {
            return {
              ...item,
              waitingPeriod: correspondingItem.waitingPeriod,
            };
          }
        }
        return item;
      });
    this.package = this.quote.products.filter((x) => {
      return x.type.toLowerCase() === ProductType.PACKAGE;
    })[0];
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_constants.scss';
@import '@general/styles/_responsive_mixins.scss';

.quote-features {
  margin-bottom: rem(50);

  h3 {
    margin-bottom: rem(24);
  }

  h4 {
    background-color: $WHITE;
    padding: 0;
    margin-bottom: rem(20);
    margin-top: rem(27);
  }

  h5 {
    font-size: font-size(0);
    padding-top: 0;
    margin-bottom: rem(12.8);
    margin-top: rem(32);
  }

  strong {
    font-weight: $BOLD;
  }

  .flexi-items {
    li {
      position: relative;

      &::before {
        position: absolute;
        top: 2px;
      }

      p:first-of-type {
        margin-bottom: 0.8rem;
      }
    }

    .flexi-limit {
      li::before {
        background: none;
      }
    }
  }

  :deep(.accordion-button) {
    max-width: none;
    padding-left: 6px;
  }

  :deep([class^='accordion-wrapper']) {
    margin-bottom: rem(24);
  }

  :deep(ul) {
    margin-bottom: rem(24);
  }
}
</style>
